// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { create } from 'zustand';
import { useGameSessionStore } from './gameSessionStore';
import { useGameStateStore } from './gameStateStore';
import { logger } from '../lib/logger';
import type { PlayerInfo, GameState, LobbyState } from '../types/game';

export interface PlayerStore {
  addPlayer: (player: PlayerInfo) => void;
  removePlayer: (playerId: string) => void;
  updatePlayerNpcStatus: (
    playerId: string,
    updates: { is_npc: boolean; display_name?: string; claim_code?: string | null }
  ) => void;
  updatePlayerProfile: (
    playerId: string,
    profile: {
      profile_emoji?: string | null;
      theme_primary_color?: string | null;
    }
  ) => void;
  reorderPlayersForHost: (newHostId: string, isCurrentPlayer: boolean) => void;
  getCurrentPlayer: () => PlayerInfo | null;
}

export const usePlayerStore = create<PlayerStore>()((_set, _get) => ({
  addPlayer: player => {
    const gameStateStore = useGameStateStore.getState();
    const gameState = gameStateStore.gameState;
    if (!gameState) return;

    const updates: Record<string, unknown> = {};
    const stateRecord = gameState as unknown as Record<string, unknown>;

    if (Array.isArray(stateRecord.current_players)) {
      const playerExists = (stateRecord.current_players as PlayerInfo[]).some(
        p => p.player_id === player.player_id
      );
      if (!playerExists) {
        updates.current_players = [...stateRecord.current_players, player];
      }
    }

    if (Array.isArray(stateRecord.all_players)) {
      const playerExists = (stateRecord.all_players as PlayerInfo[]).some(
        p => p.player_id === player.player_id
      );
      if (!playerExists) {
        updates.all_players = [...stateRecord.all_players, player];
      }
    }

    if (Array.isArray(stateRecord.players)) {
      if (!stateRecord.players.includes(player.player_id)) {
        updates.players = [...stateRecord.players, player.player_id];
      }
    }

    if (stateRecord.player_stats && typeof stateRecord.player_stats === 'object') {
      updates.player_stats = {
        ...(stateRecord.player_stats as Record<string, unknown>),
        [player.player_id]: {
          display_name: player.display_name,
          is_connected: true,
          is_npc: (player as unknown as { is_npc?: boolean }).is_npc || false,
        },
      };
    }

    logger.info('player_added', { playerId: player.player_id });
    gameStateStore.updateGameState(
      { ...gameState, ...updates } as GameState | LobbyState,
      useGameSessionStore.getState().playerId
    );
  },

  removePlayer: playerId => {
    const gameStateStore = useGameStateStore.getState();
    const gameState = gameStateStore.gameState;
    if (!gameState) return;

    const updates: Record<string, unknown> = {};
    const stateRecord = gameState as unknown as Record<string, unknown>;

    if (Array.isArray(stateRecord.current_players)) {
      updates.current_players = (stateRecord.current_players as PlayerInfo[]).filter(
        p => p.player_id !== playerId
      );
    }

    if (Array.isArray(stateRecord.all_players)) {
      updates.all_players = (stateRecord.all_players as PlayerInfo[]).filter(
        p => p.player_id !== playerId
      );
    }

    if (Array.isArray(stateRecord.players)) {
      updates.players = (stateRecord.players as string[]).filter(pid => pid !== playerId);
    }

    logger.info('player_removed', { playerId });
    gameStateStore.updateGameState(
      { ...gameState, ...updates } as GameState | LobbyState,
      useGameSessionStore.getState().playerId
    );
  },

  updatePlayerNpcStatus: (playerId, updates) => {
    const gameStateStore = useGameStateStore.getState();
    const gameState = gameStateStore.gameState;
    if (!gameState) return;

    const stateRecord = gameState as unknown as Record<string, unknown>;
    const stateUpdates: Record<string, unknown> = {};

    const applyNpcUpdate = (player: PlayerInfo) =>
      player.player_id === playerId ? { ...player, ...updates } : player;

    if (Array.isArray(stateRecord.current_players)) {
      stateUpdates.current_players = (stateRecord.current_players as PlayerInfo[]).map(
        applyNpcUpdate
      );
    }
    if (Array.isArray(stateRecord.all_players)) {
      stateUpdates.all_players = (stateRecord.all_players as PlayerInfo[]).map(applyNpcUpdate);
    }

    if (Object.keys(stateUpdates).length > 0) {
      logger.info('player_npc_status_updated', { playerId, ...updates });
      gameStateStore.updateGameState(
        { ...gameState, ...stateUpdates } as GameState | LobbyState,
        useGameSessionStore.getState().playerId
      );
    }
  },

  updatePlayerProfile: (playerId, profile) => {
    const gameStateStore = useGameStateStore.getState();
    const gameState = gameStateStore.gameState;
    if (!gameState) return;

    const updates: Record<string, unknown> = {};
    const stateRecord = gameState as unknown as Record<string, unknown>;

    if (Array.isArray(stateRecord.current_players)) {
      updates.current_players = (stateRecord.current_players as PlayerInfo[]).map(p =>
        p.player_id === playerId ? { ...p, ...profile } : p
      );
    }

    if (Array.isArray(stateRecord.all_players)) {
      updates.all_players = (stateRecord.all_players as PlayerInfo[]).map(p =>
        p.player_id === playerId ? { ...p, ...profile } : p
      );
    }

    if (Object.keys(updates).length > 0) {
      logger.info('player_profile_updated', { playerId, profile });
      gameStateStore.updateGameState(
        { ...gameState, ...updates } as GameState | LobbyState,
        useGameSessionStore.getState().playerId
      );
    }
  },

  reorderPlayersForHost: (newHostId, isCurrentPlayer) => {
    const gameStateStore = useGameStateStore.getState();
    const gameState = gameStateStore.gameState;
    if (!gameState) return;

    const updates: Record<string, unknown> = { is_host: isCurrentPlayer };
    const stateRecord = gameState as unknown as Record<string, unknown>;

    if (Array.isArray(stateRecord.all_players)) {
      const reorderedPlayers = [...(stateRecord.all_players as PlayerInfo[])];
      const newHostIndex = reorderedPlayers.findIndex(p => p.player_id === newHostId);
      if (newHostIndex > 0) {
        const [newHost] = reorderedPlayers.splice(newHostIndex, 1);
        reorderedPlayers.unshift(newHost);
      }
      updates.all_players = reorderedPlayers;
    }

    if (Array.isArray(stateRecord.current_players)) {
      const reorderedLobbyPlayers = [...(stateRecord.current_players as PlayerInfo[])];
      const newHostIndex = reorderedLobbyPlayers.findIndex(p => p.player_id === newHostId);
      if (newHostIndex > 0) {
        const [newHost] = reorderedLobbyPlayers.splice(newHostIndex, 1);
        reorderedLobbyPlayers.unshift(newHost);
      }
      updates.current_players = reorderedLobbyPlayers;
    }

    logger.info('players_reordered_for_host', { newHostId, isCurrentPlayer });
    gameStateStore.updateGameState(
      { ...gameState, ...updates } as GameState | LobbyState,
      useGameSessionStore.getState().playerId
    );
  },

  getCurrentPlayer: () => {
    const gameStateStore = useGameStateStore.getState();
    const gameState = gameStateStore.gameState;
    const playerId = useGameSessionStore.getState().playerId;

    if (!gameState || !playerId) return null;

    if ('all_players' in gameState) {
      return gameState.all_players.find(p => p.player_id === playerId) || null;
    } else if ('current_players' in gameState) {
      return gameState.current_players.find(p => p.player_id === playerId) || null;
    }

    return null;
  },
}));
